/**
 * Board Templates Table Component
 *
 * Displays board templates in a table with sortable columns,
 * delete/restore actions, and deleted row styling.
 */

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Icon, IconName } from '@/icons';
import type { GetBoardTemplatesTemplatesItem } from '@/types/generated';
import TemplatePreview from '@/components/core/Kanban/TemplatePreview';
import { BoardTag } from '@/types/enums/BoardTag';
import { useThemeColors } from '@/styles/colors/useThemeColors';
import { generateDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { formatDate } from '@/utils/date';

type Template = GetBoardTemplatesTemplatesItem;

/** BoardTag display config - title case labels and semantic colors */
const getTagDisplay = (
  colors: ReturnType<typeof useThemeColors>
): Record<string, { label: string; color: string }> => ({
  [BoardTag.PRIMARY]: { label: 'Primary', color: colors.purple },
  [BoardTag.SECONDARY]: { label: 'Secondary', color: colors.teal },
  [BoardTag.PERSONAL]: { label: 'Personal', color: colors.coral },
});

/** Extract tag from template config JSONB */
function getTemplateTag(config?: Record<string, unknown>): string | null {
  if (!config || typeof config !== 'object') return null;
  const tag = config.tag;
  const validTags: string[] = Object.values(BoardTag);
  if (typeof tag === 'string' && validTags.includes(tag)) return tag;
  return null;
}

export interface TemplatesTableProps {
  templates: Template[];
  isLoading?: boolean;
  onDelete?: (template: Template) => void;
  onRestore?: (template: Template) => void;
  onUpdateTag?: (template: Template, tag: string | null) => void;
  onEdit?: (template: Template) => void;
}

type SortKey = 'name' | 'created_at';
type SortDirection = 'asc' | 'desc';

function TableSkeleton() {
  return (
    <>
      {[1, 2, 3].map(i => (
        <TableRow key={i}>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-32" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-48" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-12" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-12" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-16" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-24" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  return (
    <TableRow>
      <TableCell colSpan={7} className="h-24 text-center">
        <div className="flex flex-col items-center justify-center text-muted-foreground">
          <p className="text-sm">No board templates found</p>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function TemplatesTable({
  templates,
  isLoading = false,
  onDelete,
  onRestore,
  onUpdateTag,
  onEdit,
}: TemplatesTableProps) {
  const colors = useThemeColors();
  const TAG_DISPLAY = getTagDisplay(colors);
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: SortDirection;
  } | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const handleSort = (key: SortKey) => {
    let direction: SortDirection = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedTemplates = [...templates].sort((a, b) => {
    if (!sortConfig) return 0;
    const { key, direction } = sortConfig;
    const aValue = a[key] ?? '';
    const bValue = b[key] ?? '';
    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const getSortIndicator = (key: SortKey) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  return (
    <div className="rounded-lg border shadow-soft overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
              onClick={() => handleSort('name')}
            >
              Name{getSortIndicator('name')}
            </TableHead>
            <TableHead className="text-left">Description</TableHead>
            <TableHead className="text-left">Columns</TableHead>
            <TableHead className="text-left">Tag</TableHead>
            <TableHead
              className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
              onClick={() => handleSort('created_at')}
            >
              Created At{getSortIndicator('created_at')}
            </TableHead>
            {(onDelete || onRestore || onEdit) && (
              <TableHead className="w-20"></TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableSkeleton />
          ) : sortedTemplates.length === 0 ? (
            <EmptyState />
          ) : (
            sortedTemplates.map(tmpl => {
              const isDeleted = !!tmpl.deleted_at;
              const isExpanded = selectedTemplateId === tmpl.id;
              const colCount = onDelete || onRestore || onEdit ? 6 : 5;
              return (
                <React.Fragment key={tmpl.id}>
                  <TableRow
                    className={`transition-colors duration-150 cursor-pointer hover:bg-accent/10 ${
                      isDeleted ? 'opacity-50' : ''
                    } ${isExpanded ? 'bg-accent/5' : ''}`}
                    onClick={() =>
                      setSelectedTemplateId(prev =>
                        prev === tmpl.id ? null : tmpl.id
                      )
                    }
                  >
                    <TableCell
                      className={`font-medium ${isDeleted ? 'line-through' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          name={IconName.CHEVRON_RIGHT}
                          size="sm"
                          className={`transition-transform duration-200 text-muted-foreground ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                        />
                        {tmpl.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[300px] truncate">
                      {tmpl.description || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="default">{tmpl.statuses.length}</Badge>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const tag = getTemplateTag(
                          tmpl.config as Record<string, unknown> | undefined
                        );
                        const isDeleted = !!tmpl.deleted_at;
                        return (
                          <select
                            value={tag || ''}
                            onChange={e => {
                              e.stopPropagation();
                              onUpdateTag?.(tmpl, e.target.value || null);
                            }}
                            onClick={e => e.stopPropagation()}
                            disabled={isDeleted || !onUpdateTag}
                            className="text-xs border rounded px-2 py-1 bg-background cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            style={
                              tag && TAG_DISPLAY[tag]
                                ? {
                                    borderColor: TAG_DISPLAY[tag].color + '60',
                                    color: TAG_DISPLAY[tag].color,
                                    backgroundColor:
                                      TAG_DISPLAY[tag].color + '10',
                                  }
                                : undefined
                            }
                          >
                            <option value="">None</option>
                            {Object.entries(TAG_DISPLAY).map(
                              ([value, { label }]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              )
                            )}
                          </select>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(tmpl.created_at)}
                    </TableCell>
                    {(onDelete || onRestore || onEdit) && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {!isDeleted && onEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-primary/10"
                              onClick={e => {
                                e.stopPropagation();
                                onEdit(tmpl);
                              }}
                              title={`Edit ${tmpl.name}`}
                            >
                              <Icon
                                name={IconName.EDIT}
                                size="sm"
                                className="text-primary/70"
                              />
                            </Button>
                          )}
                          {isDeleted && onRestore ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-primary hover:bg-primary/10"
                              onClick={e => {
                                e.stopPropagation();
                                onRestore(tmpl);
                              }}
                              title={`Restore ${tmpl.name}`}
                            >
                              Restore
                            </Button>
                          ) : !isDeleted && onDelete ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-destructive/10"
                              onClick={e => {
                                e.stopPropagation();
                                onDelete(tmpl);
                              }}
                              title={`Delete ${tmpl.name}`}
                            >
                              <Icon
                                name={IconName.TRASH}
                                size="sm"
                                className="text-destructive/70"
                              />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={colCount} className="p-0">
                        <div className="px-6 py-2 border-t bg-accent/5 space-y-3">
                          <TemplatePreview template={tmpl} />
                          {tmpl.boards && tmpl.boards.length > 0 && (
                            <div className="pt-1">
                              <p className="text-xs font-medium text-muted-foreground mb-1.5">
                                Boards using this template ({tmpl.boards.length}
                                )
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {tmpl.boards.map(board => (
                                  <a
                                    key={board.id}
                                    href={generateDeepLink(
                                      DeepLinkEntity.BOARD,
                                      board.id
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-background hover:bg-accent/20 transition-colors"
                                    style={{ color: colors.primary }}
                                    title={`Open "${board.title}" in new tab${board.template_version != null ? ` (v${board.template_version})` : ''}`}
                                  >
                                    <Icon
                                      name={IconName.EXTERNAL_LINK}
                                      size="sm"
                                    />
                                    <span>{board.title}</span>
                                    {board.template_version != null &&
                                      board.template_version < tmpl.version && (
                                        <span
                                          className="text-[10px] px-1 rounded"
                                          style={{
                                            backgroundColor:
                                              colors.coral + '20',
                                            color: colors.coral,
                                          }}
                                          title={`Board is on v${board.template_version}, template is on v${tmpl.version}`}
                                        >
                                          v{board.template_version}
                                        </span>
                                      )}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          {tmpl.boards && tmpl.boards.length === 0 && (
                            <p className="text-xs text-muted-foreground/60 italic">
                              No boards created from this template yet
                            </p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
